import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Play, Pause } from "lucide-react";
import { obtenerUrlAdjunto } from "../../../hooks/useUrlAdjuntoChatElite";

// Solo suena un mensaje de voz a la vez: al reproducir uno se pausan los demas
const EVENTO_REPRODUCIR = "ce-nota-voz:reproducir";
const VELOCIDADES = [1, 1.5, 2];
const BARRAS = 40;
// Sin forma de onda (mensajes antiguos): barras de altura fija
const ONDAS_PLANAS = Array(BARRAS).fill(35);

const formatear = (s) => {
  const total = Math.max(0, Math.round(s || 0));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
};

/**
 * Mensaje de voz con reproductor propio (igual en todos los navegadores): play/pausa, forma de onda que se colorea al
 * avanzar (clic o arrastre para saltar, flechas con el teclado), tiempo y velocidad (1×, 1,5×, 2×).
 * La URL temporal (SAS, caduca en minutos) se pide al entrar en pantalla, para que play() vaya en el mismo clic (iOS),
 * y se renueva una vez si ya ha caducado.
 * @param {{ ticketId: number, adjunto: Object, voz?: { segundos?: number, ondas?: number[] } }} props
 */
const NotaDeVoz = ({ ticketId, adjunto, voz }) => {
  const { t } = useTranslation("common");
  // Nodo en estado (no ref): si el árbol se suspende y el nodo se engancha más tarde, el efecto vuelve a ejecutarse
  const [nodo, setNodo] = useState(null);
  const audioRef = useRef(null);
  const ondaRef = useRef(null);
  const renovada = useRef(false);
  const arrastrando = useRef(false);
  const [visible, setVisible] = useState(false);
  const [url, setUrl] = useState(null);
  const [error, setError] = useState(false);
  const [sonando, setSonando] = useState(false);
  const [actual, setActual] = useState(0);
  const [duracion, setDuracion] = useState(voz?.segundos || 0);
  const [velocidad, setVelocidad] = useState(1);
  const ondas = Array.isArray(voz?.ondas) && voz.ondas.length ? voz.ondas : ONDAS_PLANAS;

  useEffect(() => {
    if (visible || !nodo) return undefined;
    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return undefined;
    }
    const observer = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) {
        setVisible(true);
        observer.disconnect();
      }
    }, { rootMargin: "200px" });
    observer.observe(nodo);
    return () => observer.disconnect();
  }, [visible, nodo]);

  useEffect(() => {
    if (!visible || url) return undefined;
    let vivo = true;
    obtenerUrlAdjunto(ticketId, adjunto.id).then((u) => vivo && setUrl(u)).catch(() => vivo && setError(true));
    return () => { vivo = false; };
  }, [visible, url, ticketId, adjunto.id]);

  // Pausar si empieza a sonar otro
  useEffect(() => {
    const alOtro = (e) => {
      if (e.detail !== audioRef.current) audioRef.current?.pause();
    };
    window.addEventListener(EVENTO_REPRODUCIR, alOtro);
    return () => window.removeEventListener(EVENTO_REPRODUCIR, alOtro);
  }, []);

  // Progreso suave mientras suena
  useEffect(() => {
    if (!sonando) return undefined;
    let id;
    const paso = () => {
      if (audioRef.current && !arrastrando.current) setActual(audioRef.current.currentTime);
      id = requestAnimationFrame(paso);
    };
    id = requestAnimationFrame(paso);
    return () => cancelAnimationFrame(id);
  }, [sonando]);

  const alFallar = () => {
    if (renovada.current) return setError(true);
    renovada.current = true;
    obtenerUrlAdjunto(ticketId, adjunto.id).then(setUrl).catch(() => setError(true));
    return undefined;
  };

  const alternar = () => {
    const a = audioRef.current;
    if (!a) return;
    if (!a.paused) {
      a.pause();
      return;
    }
    window.dispatchEvent(new CustomEvent(EVENTO_REPRODUCIR, { detail: a }));
    a.playbackRate = velocidad;
    a.play().catch(() => {});
  };

  const saltarA = useCallback((segundos) => {
    const a = audioRef.current;
    const total = duracion || a?.duration || 0;
    if (!a || !total) return;
    const s = Math.min(Math.max(segundos, 0), total);
    a.currentTime = s;
    setActual(s);
  }, [duracion]);

  const posicionDesdePuntero = (e) => {
    const r = ondaRef.current.getBoundingClientRect();
    return ((e.clientX - r.left) / r.width) * (duracion || 0);
  };

  const alPulsarOnda = (e) => {
    if (!url || error) return;
    arrastrando.current = true;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    saltarA(posicionDesdePuntero(e));
  };
  const alMoverOnda = (e) => {
    if (arrastrando.current) saltarA(posicionDesdePuntero(e));
  };
  const alSoltarOnda = () => { arrastrando.current = false; };

  const alTeclaOnda = (e) => {
    if (e.key === "ArrowRight") { e.preventDefault(); saltarA(actual + 5); }
    if (e.key === "ArrowLeft") { e.preventDefault(); saltarA(actual - 5); }
    if (e.key === " " || e.key === "Enter") { e.preventDefault(); alternar(); }
  };

  const cambiarVelocidad = () => {
    const siguiente = VELOCIDADES[(VELOCIDADES.indexOf(velocidad) + 1) % VELOCIDADES.length];
    setVelocidad(siguiente);
    if (audioRef.current) audioRef.current.playbackRate = siguiente;
  };

  const progreso = duracion ? Math.min(actual / duracion, 1) : 0;
  const listo = !!url && !error;
  // Mientras suena (o si ya se avanzó) el tiempo transcurrido; si no, la duración, como WhatsApp
  const tiempo = sonando || actual > 0 ? actual : duracion;

  return (
    <div ref={setNodo} className={`ce-voz${sonando ? " ce-voz--sonando" : ""}`} aria-label={t("chatElite.adjuntos.mensajeVoz")}>
      <button
        type="button"
        className="ce-voz__play"
        onClick={alternar}
        disabled={!listo}
        aria-label={t(sonando ? "chatElite.adjuntos.pausar" : "chatElite.adjuntos.reproducir")}
      >
        {sonando ? <Pause size={16} fill="currentColor" /> : <Play size={16} fill="currentColor" />}
      </button>
      <div className="ce-voz__cuerpo">
        {error ? (
          <span className="ce-voz__error">{t("chatElite.adjuntos.audioNoDisponible")}</span>
        ) : (
          <div
            ref={ondaRef}
            className="ce-voz__onda"
            role="slider"
            tabIndex={listo ? 0 : -1}
            aria-label={t("chatElite.adjuntos.posicionAudio")}
            aria-valuemin={0}
            aria-valuemax={Math.round(duracion)}
            aria-valuenow={Math.round(actual)}
            aria-valuetext={`${formatear(actual)} / ${formatear(duracion)}`}
            onPointerDown={alPulsarOnda}
            onPointerMove={alMoverOnda}
            onPointerUp={alSoltarOnda}
            onPointerCancel={alSoltarOnda}
            onKeyDown={alTeclaOnda}
          >
            {ondas.map((v, i) => (
              <span
                key={i}
                className={`ce-voz__barra${(i + 0.5) / ondas.length <= progreso ? " ce-voz__barra--oida" : ""}`}
                style={{ height: `${Math.max(12, Math.min(100, v))}%` }}
              />
            ))}
          </div>
        )}
        <div className="ce-voz__pie">
          <span className="ce-voz__tiempo">{formatear(tiempo)}</span>
          {listo && (
            <button type="button" className="ce-voz__velocidad" onClick={cambiarVelocidad} aria-label={t("chatElite.adjuntos.velocidad")}>
              {String(velocidad).replace(".", ",")}×
            </button>
          )}
        </div>
      </div>
      {url && !error && (
        // eslint-disable-next-line jsx-a11y/media-has-caption
        <audio
          ref={audioRef}
          src={url}
          preload="metadata"
          onLoadedMetadata={(e) => { if (Number.isFinite(e.currentTarget.duration)) setDuracion(e.currentTarget.duration); }}
          onPlay={() => setSonando(true)}
          onPause={() => setSonando(false)}
          onEnded={() => { setSonando(false); setActual(0); if (audioRef.current) audioRef.current.currentTime = 0; }}
          onError={alFallar}
        />
      )}
    </div>
  );
};

export default NotaDeVoz;
